import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ProductCard from "@/components/ProductCard";
import SEO, { buildBreadcrumbLd } from "@/components/SEO";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { ChevronRight, ShieldCheck, Truck, Wrench } from "lucide-react";

/**
 * SEO-friendly brand landing page at /marca/{brand-slug}
 * Provides unique H1, intro paragraph, JSON-LD breadcrumb and a filtered product grid.
 */
export default function BrandLanding() {
  const { brand } = useParams();
  const { lang } = useLang();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const brandName = useMemo(() => brand ? brand.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "", [brand]);

  useEffect(() => {
    if (!brand) return;
    setLoading(true);
    api
      .get("/products", { params: { brand: brandName } })
      .then((r) => setProducts(r.data || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [brand, brandName]);

  const heroImage = products[0]?.images?.[0];
  const minPrice = products.reduce((min, p) => (p.price && (!min || p.price < min) ? p.price : min), 0);
  const path = `/marca/${brand}`;

  return (
    <div className="min-h-screen bg-xd-light">
      <SEO
        title={`Autoradio Android per ${brandName}`}
        description={`Scopri gli autoradio Android Plug&Play dedicati alla tua ${brandName}. ${products.length} modelli compatibili, installazione in 30 minuti, garanzia 24 mesi, spedizione gratuita da 99€.`}
        image={heroImage}
        path={path}
        lang={lang}
        jsonLd={[
          buildBreadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Catalogo", path: "/catalogo" },
            { name: brandName, path },
          ]),
        ]}
        hreflangs={{ it: path, en: path, fr: path, de: path, es: path }}
      />
      <Header />

      <section className="bg-xd-dark text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
          <nav className="text-xs text-slate-400 mb-3 flex items-center gap-1 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/catalogo" className="hover:text-white">Catalogo</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">{brandName}</span>
          </nav>
          <div className="font-mono-tech text-xs uppercase tracking-widest text-xd-cyan">Autoradio dedicate</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-2 tracking-tight" data-testid="brand-h1">
            Autoradio Android Plug&Play per {brandName}
          </h1>
          <p className="mt-4 text-slate-300 leading-relaxed max-w-3xl">
            {products.length > 0 ? (
              <>Scopri {products.length} autoradio Android dedicati alla tua {brandName}, con installazione Plug&Play in 30 minuti, comandi al volante, Apple CarPlay e Android Auto integrati. Prezzi a partire da <strong className="text-white">€ {Number(minPrice).toFixed(2)}</strong>, spedizione express 24-48h e garanzia 24 mesi inclusa.</>
            ) : (
              <>Stiamo aggiungendo nuovi modelli dedicati a {brandName}. Contattaci su WhatsApp per verificare la compatibilità del tuo modello specifico.</>
            )}
          </p>
          <div className="mt-6 flex gap-4 text-sm text-slate-300 flex-wrap">
            <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-xd-cyan" /> Garanzia 24 mesi</span>
            <span className="inline-flex items-center gap-2"><Truck className="w-4 h-4 text-xd-cyan" /> Spedizione 24-48h</span>
            <span className="inline-flex items-center gap-2"><Wrench className="w-4 h-4 text-xd-cyan" /> Installazione Plug&Play</span>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        {loading ? (
          <div className="text-center text-xd-muted">Caricamento…</div>
        ) : products.length === 0 ? (
          <div className="text-center py-14">
            <p className="text-xd-muted">Nessun prodotto disponibile al momento per {brandName}.</p>
            <Link to="/catalogo" className="mt-4 inline-block text-xd-cyanHover font-medium hover:underline">
              Sfoglia tutto il catalogo →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="brand-products-grid">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
